import { useState } from "react";
import { formatRub, formatUsdt, maskMiddle } from "@/lib/format";
import { useCopy, CopyIcon } from "@/components/copy";
import type { Account, UsdtAsset } from "@/lib/api";

// Портфель клиента: общий баланс (в выбранной валюте — RUB или USD) и список активов
// ниже — рубли и USDT (у USDT дополнительно адрес кошелька). Панель НЕПРОЗРАЧНАЯ и тёмная,
// чтобы контрастировать с зелёным стеклом на фоне профиля, а не сливаться с ним.
type Currency = "RUB" | "USD";

export function AccountCard({
  account,
  usdt,
  rate,
}: {
  account: Account;
  usdt: UsdtAsset | null;
  rate: number | null;
}) {
  const [cur, setCur] = useState<Currency>("RUB");

  const rub = account.balance_rub;
  const hasUsdt = usdt != null; // USDT-актив показываем только читерам (бэкенд отдаёт usdt лишь им)
  const usdtBal = usdt?.balance ?? 0;
  // конвертации возможны только при известном курсе (₽ за 1 USDT) И наличии USDT-актива;
  // иначе общий баланс = рубли, переключатель валют прячем.
  const canConvert = hasUsdt && rate != null && rate > 0;
  const usdtInRub = canConvert ? usdtBal * (rate as number) : 0;
  const rubInUsd = canConvert ? rub / (rate as number) : 0;

  const totalRub = rub + usdtInRub;
  const totalUsd = rubInUsd + usdtBal;
  const totalText = cur === "RUB" ? formatRub(totalRub) : formatUsdt(totalUsd);

  return (
    <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-surface-card p-5 shadow-2xl shadow-black/60 ring-1 ring-inset ring-white/[0.04]">
      {/* глубина: изумрудная подложка + сияние в углу + верхний блик */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand/[0.14] via-transparent to-black/30" />
      <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-brand/25 blur-3xl" />
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

      <div className="relative">
        {/* Шапка: «Общий баланс» + переключатель валюты отображения */}
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-white/40">Общий баланс</div>
          {canConvert && <CurrencyToggle cur={cur} setCur={setCur} />}
        </div>
        <div className="mt-1.5 text-[36px] font-semibold leading-none tracking-tight [text-shadow:0_2px_12px_rgba(0,0,0,0.5)]">
          {totalText}
        </div>

        {/* Активы */}
        <div className="mt-6 space-y-2.5">
          <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-white/40">Активы</div>

          <AssetRow
            badge="₽"
            badgeClass="from-brand-400 to-brand-600 text-surface-base"
            title="Рубли"
            subtitle="RUB"
            primary={formatRub(rub)}
            secondary={canConvert ? `≈ ${formatUsdt(rubInUsd)}` : null}
          />

          {hasUsdt && (
            <AssetRow
              badge="₮"
              badgeClass="from-teal-300 to-teal-500 text-surface-base"
              title="Tether"
              subtitle="USDT · TRC20"
              primary={formatUsdt(usdtBal)}
              secondary={canConvert ? `≈ ${formatRub(usdtInRub)}` : null}
              address={usdt?.address ?? null}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CurrencyToggle({ cur, setCur }: { cur: Currency; setCur: (c: Currency) => void }) {
  return (
    <div className="flex rounded-full bg-white/[0.06] p-0.5 text-[11px] font-semibold ring-1 ring-inset ring-white/10">
      {(["RUB", "USD"] as const).map((c) => (
        <button
          key={c}
          onClick={() => setCur(c)}
          className={`rounded-full px-2.5 py-1 tracking-wider transition ${
            cur === c ? "bg-white text-surface-base" : "text-white/60"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function AssetRow({
  badge,
  badgeClass,
  title,
  subtitle,
  primary,
  secondary,
  address,
}: {
  badge: string;
  badgeClass: string;
  title: string;
  subtitle: string;
  primary: string;
  secondary: string | null;
  address?: string | null;
}) {
  return (
    <div className="rounded-2xl bg-white/[0.04] px-3.5 py-3 ring-1 ring-inset ring-white/[0.05]">
      <div className="flex items-center gap-3">
        <div
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-[15px] font-bold shadow-md shadow-black/20 ${badgeClass}`}
        >
          {badge}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold leading-tight">{title}</div>
          <div className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">{subtitle}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[15px] font-semibold tabular-nums leading-tight">{primary}</div>
          {secondary && <div className="mt-0.5 text-[11px] tabular-nums text-white/40">{secondary}</div>}
        </div>
      </div>
      {address && <WalletAddress address={address} />}
    </div>
  );
}

function WalletAddress({ address }: { address: string }) {
  const { copied, copy } = useCopy(address);
  return (
    <button
      onClick={copy}
      className="mt-2.5 flex w-full items-center gap-2 rounded-xl bg-black/25 px-3 py-2 text-left ring-1 ring-inset ring-white/[0.06] active:opacity-80"
    >
      <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/35">Адрес</span>
      <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-white/80">{maskMiddle(address, 8, 8)}</span>
      <span className="flex shrink-0 items-center text-brand">
        {copied ? <span className="text-[13px] font-semibold leading-none">✓</span> : <CopyIcon className="h-3.5 w-3.5" />}
      </span>
    </button>
  );
}
